import { useEffect, useRef, useState } from 'react';
import { useQuery } from 'react-query';
import { REQUEST_ACTIONS } from '~/Constants/Actions.constants';
import { ADAPTER_SVC_PARTNER_PORTAL_URLS } from '~/Constants/Urls.constants';
import {
  requestReactQueryRaw,
  getStatusMessage,
  checkIsAborted,
  generateUUID,
} from '~/Helpers/ReactQuery.helper';
import { routeReplacer } from '~/Helpers/String.helper';
import { ToastVariant, useToastMessage } from '~/Hooks';

interface IUseFetchClassesProps {
  onSuccess?: Function;
}

type FetchClassesResponse = {
  message: string;
  toastVariant: ToastVariant;
  status: number;
  isAborted: boolean;
};

export const useFetchClasses = ({ onSuccess } = {} as IUseFetchClassesProps) => {
  const abortControllerRef = useRef(new AbortController());
  const [channelId, setChannelId] = useState('');
  const [isRetryActive, setIsRetryActive] = useState(true);
  const { showToast } = useToastMessage();
  useEffect(() => () => abortControllerRef.current.abort(), []);

  const fetchClasses = async (): Promise<FetchClassesResponse> => {
    const response = (await new Promise((resolve) => {
      requestReactQueryRaw({
        url: routeReplacer(ADAPTER_SVC_PARTNER_PORTAL_URLS.FETCH_CHANNEL_CLASSES, {
          channelId,
        }),
        config: { signal: abortControllerRef.current.signal },
      })
        .then((res) => resolve(res))
        .catch((err) => resolve({ ...err?.response, isAborted: !!err && checkIsAborted(err) }));
    })) as any;

    const { toastVariant, message, status, isAborted } = getStatusMessage(response as any);
    const dismissTimeout = isRetryActive ? 3000 : 60000;

    showToast(generateUUID(), message, toastVariant, false, undefined, dismissTimeout);

    if ([202].includes(status) || isAborted) {
      throw { toastVariant, message, status, isAborted };
    }
    if (status === 200) {
      onSuccess && onSuccess();
    }
    return { toastVariant, message, status, isAborted };
  };

  const result = useQuery<FetchClassesResponse, FetchClassesResponse>(
    [REQUEST_ACTIONS.FETCH_CHANNEL_CLASSES],
    fetchClasses,
    {
      retry: (failureCount: number, response: FetchClassesResponse) => {
        if (!isRetryActive) {
          return isRetryActive;
        }
        const { status, isAborted } = response;
        return !isAborted && ([202].includes(status) || status >= 400) && failureCount < 9;
      },
      retryDelay: 10000,
      enabled: false, // Disabled by default; we'll trigger refetch with channelId manually
    },
  );

  const fetchClassesWithChannelId = async (channelId: string, retry = true) => {
    if (channelId === '') return;
    abortControllerRef.current = new AbortController(); // Reset the abort controller
    if (retry !== isRetryActive) {
      await setIsRetryActive(retry);
    }
    await setChannelId(channelId);
    // await queryClient.invalidateQueries(REQUEST_ACTIONS.FETCH_CHANNEL_CLASSES); // Invalidate the cache for the key
    result?.refetch();
  };

  return {
    ...result,
    isLoading: result?.isLoading || result?.isFetching,
    refetch: fetchClassesWithChannelId,
  };
};
